// Pure, declarative per-type descriptor for the assessment over-box menu.
// No React/redux here — the AssessmentBoxMenu component renders rows by their
// `control` type, and each `key` maps 1:1 to the plugin's toolbar state key
// (verified against each plugin's getToolbar; see
// docs/superpowers/specs/2026-06-07-assessment-canvas-menu-design.md).

export const EVAL_PLUGIN_IDS = [
  "MultipleChoice",
  "MultipleAnswer",
  "TrueFalse",
  "FillInBlank",
  "FreeResponse",
  "DragDrop",
  "Matching",
  "Ordering",
];

export function isEvalPlugin(pluginId) {
  return EVAL_PLUGIN_IDS.includes(pluginId);
}

// control: "stepper" | "segmented" | "toggle" | "number" | "buttons"
// `label` values are i18n keys (resolved with i18n.t in AssessmentBoxMenu).
const toggle = (key, label) => ({ key, label, control: "toggle" });
const count = (key, label) => ({ key, label, control: "stepper" });
const lettersRow = {
  key: "letters",
  label: "AssessmentMenu.labels",
  control: "segmented",
  options: [
    { value: "letters", label: "A B C" },
    { value: "numbers", label: "1 2 3" },
  ],
};
const buttonsRow = { key: "buttons", label: "AssessmentMenu.buttons", control: "buttons" };

const MENUS = {
  MultipleChoice: {
    showAddRemove: true,
    rows: [
      count("nBoxes", "AssessmentMenu.nAnswers"),
      lettersRow,
      toggle("shuffle", "AssessmentMenu.shuffleAnswers"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      buttonsRow,
    ],
  },
  MultipleAnswer: {
    showAddRemove: true,
    rows: [
      count("nBoxes", "AssessmentMenu.nAnswers"),
      lettersRow,
      toggle("shuffle", "AssessmentMenu.shuffleAnswers"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      toggle("allowPartialScore", "AssessmentMenu.partialCredit"),
      buttonsRow,
    ],
  },
  TrueFalse: {
    showAddRemove: true,
    rows: [
      count("nBoxes", "AssessmentMenu.nStatements"),
      toggle("shuffle", "AssessmentMenu.shuffle"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      toggle("allowPartialScore", "AssessmentMenu.partialCredit"),
      buttonsRow,
    ],
  },
  Ordering: {
    showAddRemove: true,
    rows: [
      count("nBoxes", "AssessmentMenu.nItems"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      toggle("allowPartialScore", "AssessmentMenu.partialCredit"),
      buttonsRow,
    ],
  },
  Matching: {
    showAddRemove: true,
    rows: [
      count("nBoxes", "AssessmentMenu.nPairs"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      toggle("allowPartialScore", "AssessmentMenu.partialCredit"),
      buttonsRow,
    ],
  },
  DragDrop: {
    showAddRemove: true,
    rows: [
      count("nBoxes", "AssessmentMenu.nItems"),
      toggle("shuffle", "AssessmentMenu.shuffle"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      buttonsRow,
    ],
  },
  FillInBlank: {
    showAddRemove: true,
    rows: [
      count("nBoxes", "AssessmentMenu.nBlanks"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      toggle("allowPartialScore", "AssessmentMenu.partialCredit"),
      buttonsRow,
    ],
  },
  FreeResponse: {
    showAddRemove: false,
    rows: [
      // maxKey/minKey: the sibling row's state value further bounds this one,
      // so min length can never exceed max length.
      {
        key: "minLength",
        label: "AssessmentMenu.minLength",
        control: "number",
        min: 0,
        max: 1000,
        maxKey: "maxLength",
      },
      {
        key: "maxLength",
        label: "AssessmentMenu.maxLength",
        control: "number",
        min: 1,
        max: 1000,
        minKey: "minLength",
      },
      // excludeSpaces: when on, whitespace isn't counted toward min/max length.
      // (Not `characters` — that key drives case/accent-insensitive grading.)
      toggle("excludeSpaces", "AssessmentMenu.countCharacters"),
      toggle("showFeedback", "AssessmentMenu.showFeedback"),
      buttonsRow,
    ],
  },
};

export function getAssessmentMenu(pluginId) {
  const m = MENUS[pluginId];
  return m ? { showAddRemove: m.showAddRemove, rows: m.rows } : null;
}
